import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError, eur, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  MapPin, Navigation, Play, CheckCircle2, Plus, Trash2, FileUp, Printer, Camera, PenLine,
  Receipt, FileDown, Mail,
} from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";
import { AuthImage } from "@/components/AuthImage";
import { clearDraft, enqueuePhoto, getDraft, pendingPhotos, saveDraft, sendOrQueue } from "@/lib/offline";

export default function InterventoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [iv, setIv] = useState(null);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({ work_done: "", hours: 0, signature_name: "", billable: true });
  const [signing, setSigning] = useState(false);
  const [uploading, setUploading] = useState("");
  const [queuedPhotos, setQueuedPhotos] = useState([]);

  useEffect(() => {
    const refresh = () => setQueuedPhotos(pendingPhotos(id));
    refresh();
    window.addEventListener("gs-queue-change", refresh);
    return () => window.removeEventListener("gs-queue-change", refresh);
  }, [id]);

  const saveSignature = async (dataUrl) => {
    setSigning(true);
    try {
      await api.post(`/interventions/${id}/signature`, { image: dataUrl, signature_name: form.signature_name });
      setMsg("Firma del cliente salvata.");
      await load();
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  const uploadPhoto = async (phase, file) => {
    if (!file) return;
    setUploading(phase);
    try {
      if (!navigator.onLine) {
        await enqueuePhoto(id, phase, file);
        setQueuedPhotos(pendingPhotos(id));
        setMsg("Offline: foto salvata sul dispositivo, verrà caricata al ritorno della rete.");
        return;
      }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("phase", phase);
      await api.post(`/interventions/${id}/photos`, fd);
      await load();
    } catch (e) {
      if (!e.response) {
        await enqueuePhoto(id, phase, file);
        setQueuedPhotos(pendingPhotos(id));
        setMsg("Rete assente: foto in coda, sarà caricata appena possibile.");
      } else {
        setMsg(apiError(e.response?.data?.detail));
      }
    } finally {
      setUploading("");
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/interventions/${id}`);
      setIv(data);
      const draft = getDraft(id);
      setForm(draft || {
        work_done: data.work_done || "",
        hours: data.hours || 0,
        signature_name: data.signature_name || "",
        billable: data.billable,
      });
    } catch (e) {
      if (!e.response) {
        setLoadError("Nessuna connessione: apri l'intervento dall'agenda quando torni online.");
        return;
      }
      setLoadError(apiError(e.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => {
    load();
    api.get("/products", { params: { kind: "ricambio" } }).then(({ data }) => setProducts(data));
  }, [load]);

  const partFields = [
    {
      name: "product_id", label: "Ricambio da catalogo", type: "select", full: true,
      options: products.map((p) => ({ value: p.id, label: `${p.code} — ${p.name} (${eur(p.price)})` })),
    },
    { name: "name", label: "Descrizione (se fuori catalogo)", full: true },
    { name: "quantity", label: "Quantità", type: "number" },
    { name: "unit_price", label: "Prezzo unitario €", type: "number" },
    { name: "chargeable", label: "Addebita al cliente", type: "switch", hint: "Se disattivo il ricambio è in garanzia/contratto" },
  ];

  const addPart = async (values) => {
    try {
      const { data } = await api.post(`/interventions/${id}/parts`, values);
      setIv((p) => ({ ...p, ...data }));
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const setStatus = async (status) => {
    await setStatusOffline(status);
  };

  const saveWork = async () => {
    const body = { ...form, hours: Number(form.hours) };
    try {
      const res = await sendOrQueue("put", `/interventions/${id}`, body);
      if (res.queued) {
        saveDraft(id, body);
        setMsg("Nessuna connessione: rapportino salvato sul dispositivo, verrà sincronizzato appena torni online.");
        return;
      }
      clearDraft(id);
      setIv((p) => ({ ...p, ...res.data }));
      setMsg("Rapportino salvato.");
      await load();
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };

  const setStatusOffline = async (status) => {
    const res = await sendOrQueue("put", `/interventions/${id}`, { status });
    if (res.queued) {
      setIv((p) => ({ ...p, status }));
      setMsg("Offline: cambio di stato salvato sul dispositivo.");
      return;
    }
    setIv((p) => ({ ...p, ...res.data }));
    await load();
  };

  const invoice = async () => {
    const { data } = await api.post(`/sync/interventions/${id}/invoice`);
    setMsg(`Documento ${data.document_id} creato su Business Experience (${data.status}).`);
  };

  const doInvoice = async () => {
    try {
      const { data } = await api.post(`/interventions/${id}/invoice`);
      setIv((p) => ({ ...p, ...data }));
      setMsg(`Fatturato: proforma ${data.invoice_code}.`);
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };

  const downloadInvoicePdf = async () => {
    try {
      const { data } = await api.get(`/interventions/${id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${iv.invoice_code || "proforma"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg("Download della proforma non riuscito.");
    }
  };

  const sendInvoice = async () => {
    try {
      const { data } = await api.post(`/interventions/${id}/send-invoice`);
      setMsg(`Proforma inviata a ${data.sent_to}.`);
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };


  if (loadError)
    return (
      <div data-testid="intervento-error">
        <PageHeader title="Intervento non disponibile" subtitle="Accesso negato" />
        <p className="text-sm text-destructive">{loadError}</p>
        <Button asChild variant="outline" className="rounded-sm mt-4">
          <Link to="/interventi">Torna agli interventi</Link>
        </Button>
      </div>
    );
  if (!iv) return <p className="overline">Caricamento…</p>;
  const canEdit = user?.role === "azienda" || (user?.role === "tecnico" && iv.status !== "completato");
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${iv.address || ""} ${iv.city || ""}`
  )}`;

  return (
    <div data-testid="intervento-detail-page">
      <PageHeader title={`Intervento ${iv.code}`} subtitle={`${iv.type} · ${iv.status.replace("_", " ")}`}>
        <Button asChild variant="outline" className="h-11 rounded-sm">
          <Link to={`/interventi/${id}/rapportino`} data-testid="open-report-btn">
            <Printer className="h-4 w-4 mr-2" /> Rapportino
          </Link>
        </Button>
        {canEdit && iv.status !== "in_corso" && iv.status !== "completato" && (
          <Button className="h-11 rounded-sm" onClick={() => setStatus("in_corso")} data-testid="start-intervention-btn">
            <Play className="h-4 w-4 mr-2" /> Inizia
          </Button>
        )}
        {canEdit && iv.status === "in_corso" && (
          <Button className="h-11 rounded-sm" onClick={() => setStatus("completato")} data-testid="complete-intervention-btn">
            <CheckCircle2 className="h-4 w-4 mr-2" /> Completa
          </Button>
        )}
        {user?.role === "azienda" && iv.status === "completato" && (
          <Button variant="outline" className="h-11 rounded-sm" onClick={invoice} data-testid="invoice-intervention-btn">
            <FileUp className="h-4 w-4 mr-2" /> Invia a Business Experience
          </Button>
        )}
      </PageHeader>

      {msg && <p className="mb-4 text-sm font-medium text-primary" data-testid="intervention-message">{msg}</p>}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 rounded-sm shadow-none" data-testid="intervento-info">
            <p className="overline">Cliente e impianto</p>
            <h3 className="font-display text-2xl font-black tracking-tighter mt-1">{iv.customer_name}</h3>
            <p className="text-sm text-muted-foreground mt-1">{iv.plant_name || "Nessun impianto collegato"}</p>
            <p className="text-sm mt-3 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" /> {iv.address || "—"}, {iv.city || "—"}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="secondary" className="rounded-sm">{itDate(iv.scheduled_date)} {iv.scheduled_time}</Badge>
              <Badge variant="secondary" className="rounded-sm">Tecnico: {iv.technician_name || "—"}</Badge>
              <Badge variant={iv.under_contract ? "default" : "outline"} className="rounded-sm">
                {iv.under_contract ? "in contratto" : "fuori contratto"}
              </Badge>
              <Badge variant="outline" className="rounded-sm capitalize">priorità {iv.priority}</Badge>
            </div>
            {iv.description && <p className="text-sm mt-4 border-l-2 border-primary pl-3">{iv.description}</p>}
            <Button asChild variant="outline" size="sm" className="rounded-sm mt-4">
              <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="navigate-btn">
                <Navigation className="h-3.5 w-3.5 mr-2" /> Naviga
              </a>
            </Button>
          </Card>

          <Card className="p-6 rounded-sm shadow-none" data-testid="rapportino-card">
            <p className="overline">Rapportino tecnico</p>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-4">Attività svolte</h3>
            <div className="space-y-4">
              <Textarea
                value={form.work_done}
                onChange={(e) => setForm({ ...form, work_done: e.target.value })}
                placeholder="Descrivi le attività eseguite…"
                rows={5}
                className="rounded-sm"
                disabled={!canEdit}
                data-testid="work-done-input"
              />
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="overline">Ore lavorate</Label>
                  <Input type="number" step="0.5" value={form.hours} disabled={!canEdit}
                         onChange={(e) => setForm({ ...form, hours: e.target.value })}
                         className="h-11 rounded-sm" data-testid="hours-input" />
                </div>
                <div className="space-y-2">
                  <Label className="overline">Firma cliente</Label>
                  <Input value={form.signature_name} disabled={!canEdit}
                         onChange={(e) => setForm({ ...form, signature_name: e.target.value })}
                         className="h-11 rounded-sm" placeholder="Nome e cognome"
                         data-testid="signature-input" />
                </div>
                <div className="space-y-2">
                  <Label className="overline">Addebitabile</Label>
                  <div className="flex items-center h-11 gap-3">
                    <Switch checked={!!form.billable} disabled={!canEdit}
                            onCheckedChange={(v) => setForm({ ...form, billable: v })}
                            data-testid="billable-switch" />
                    <span className="text-sm text-muted-foreground">{form.billable ? "Sì" : "No"}</span>
                  </div>
                </div>
              </div>
              {canEdit && (
                <Button className="rounded-sm h-12" onClick={saveWork} data-testid="save-report-btn">
                  Salva rapportino
                </Button>
              )}
            </div>
          </Card>

          <Card className="p-6 rounded-sm shadow-none" data-testid="foto-card">
            <p className="overline">Documentazione fotografica</p>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-4">Foto prima e dopo</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {["prima", "dopo"].map((phase) => (
                <div key={phase}>
                  <p className="overline mb-2">{phase === "prima" ? "Prima del lavoro" : "Dopo il lavoro"}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {(iv.photos || []).filter((p) => p.phase === phase).map((p) => (
                      <div key={p.id} className="relative group">
                        <AuthImage
                          path={p.storage_path}
                          alt={p.caption || phase}
                          className="h-24 w-full object-cover border border-border"
                          testid={`photo-${p.id}`}
                        />
                        {canEdit && (
                          <button
                            type="button"
                            onClick={async () => { await api.delete(`/photos/${p.id}`); load(); }}
                            className="absolute top-1 right-1 bg-background/90 p-1 border border-border"
                            data-testid={`delete-photo-${p.id}`}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </button>
                        )}
                      </div>
                    ))}
                    {queuedPhotos.filter((p) => p.phase === phase).map((p) => (
                      <div key={p.id} className="relative" data-testid={`queued-photo-${p.id}`}>
                        <img src={p.dataUrl} alt="in attesa" className="h-24 w-full object-cover border border-dashed border-primary opacity-70" />
                        <span className="absolute bottom-0 left-0 right-0 bg-primary text-primary-foreground text-[9px] text-center uppercase tracking-wider py-0.5">
                          in coda
                        </span>
                      </div>
                    ))}
                  </div>
                  {canEdit && (
                    <label className="mt-3 flex items-center justify-center gap-2 h-12 border border-dashed border-border cursor-pointer text-sm font-medium hover:border-primary transition-colors duration-200">
                      <Camera className="h-4 w-4" />
                      {uploading === phase ? "Caricamento…" : `Aggiungi foto ${phase}`}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => uploadPhoto(phase, e.target.files?.[0])}
                        data-testid={`upload-photo-${phase}`}
                      />
                    </label>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 rounded-sm shadow-none" data-testid="firma-card">
            <p className="overline">Accettazione cliente</p>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-4">Firma digitale</h3>
            {iv.signature_path ? (
              <div>
                <AuthImage
                  path={iv.signature_path}
                  alt="Firma cliente"
                  className="h-32 w-full max-w-sm object-contain bg-white border border-border"
                  testid="signature-image"
                />
                <p className="text-sm mt-2">{iv.signature_name || "Firma acquisita"}</p>
                {canEdit && (
                  <Button variant="outline" size="sm" className="rounded-sm mt-3"
                          onClick={async () => {
                            await api.post(`/interventions/${id}/signature/reset`);
                            await load();
                          }}
                          data-testid="resign-btn">
                    <PenLine className="h-3.5 w-3.5 mr-2" /> Firma di nuovo
                  </Button>
                )}
              </div>
            ) : canEdit ? (
              <SignaturePad onSave={saveSignature} saving={signing} />
            ) : (
              <p className="text-sm text-muted-foreground">Firma non ancora acquisita.</p>
            )}
          </Card>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xl font-bold tracking-tight">Ricambi utilizzati</h3>
              {canEdit && (
                <Button size="sm" className="rounded-sm" onClick={() => setOpen(true)} data-testid="add-part-btn">
                  <Plus className="h-4 w-4 mr-2" /> Aggiungi ricambio
                </Button>
              )}
            </div>
            <ListCard testid="ricambi-list">
              {(iv.parts || []).map((p) => (
                <div key={p.id} className="px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{p.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.quantity} × {eur(p.unit_price)}{" "}
                      {p.chargeable ? "· addebitato" : "· non addebitato (contratto/garanzia)"}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm">{p.chargeable ? eur(p.quantity * p.unit_price) : "—"}</span>
                    {canEdit && (
                      <>
                        <Switch
                          checked={p.chargeable}
                          onCheckedChange={async (v) => {
                            await api.put(`/parts/${p.id}`, { chargeable: v });
                            load();
                          }}
                          data-testid={`toggle-part-${p.id}`}
                        />
                        <Button variant="ghost" size="icon" className="text-destructive"
                                onClick={async () => { await api.delete(`/parts/${p.id}`); load(); }}
                                data-testid={`delete-part-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {(iv.parts || []).length === 0 && <Empty text="Nessun ricambio inserito." />}
            </ListCard>
          </div>
        </div>

        <Card className="p-6 rounded-sm shadow-none h-fit lg:sticky lg:top-6" data-testid="totali-card">
          <p className="overline">Riepilogo economico</p>
          <p className="font-display text-4xl font-black tracking-tighter mt-2" data-testid="total-amount">
            {eur(iv.totals?.total)}
          </p>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between border-b border-border pb-2">
              <span className="text-muted-foreground">Manodopera ({iv.hours || 0} h × {eur(iv.hourly_rate)})</span>
              <span className="font-mono">{eur(iv.totals?.labour)}</span>
            </div>
            <div className="flex justify-between border-b border-border pb-2">
              <span className="text-muted-foreground">Ricambi addebitati</span>
              <span className="font-mono">{eur(iv.totals?.parts_total)}</span>
            </div>
            <div className="flex justify-between border-b border-border pb-2">
              <span className="text-muted-foreground">Diritto di chiamata</span>
              <span className="font-mono">{eur(iv.totals?.travel)}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-5 leading-relaxed">
            {iv.under_contract
              ? "Intervento in contratto: manodopera e trasferta incluse, si addebitano solo i ricambi segnati come addebitabili."
              : "Intervento fuori contratto: manodopera, ricambi e diritto di chiamata a carico del cliente."}
          </p>

          {iv.invoice_status && (
            <div className="mt-6 pt-4 border-t border-border" data-testid="fatturazione-card">
              <div className="flex items-center justify-between mb-3">
                <p className="overline">Fatturazione</p>
                <Badge variant={iv.invoice_status === "incassato" ? "secondary" : "default"}
                       className="rounded-sm capitalize">
                  {iv.invoice_status.replace("_", " ")}
                </Badge>
              </div>
              {iv.invoice_code && (
                <p className="text-xs text-muted-foreground mb-3 font-mono">{iv.invoice_code}</p>
              )}
              {user?.role === "azienda" && (
                <div className="flex flex-wrap gap-2">
                  {iv.invoice_status === "da_fatturare" && (
                    <Button size="sm" className="rounded-sm" onClick={doInvoice} data-testid="invoice-btn">
                      <Receipt className="h-3.5 w-3.5 mr-2" /> Fattura
                    </Button>
                  )}
                  {iv.invoice_status !== "da_fatturare" && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={downloadInvoicePdf}
                              data-testid="invoice-pdf-btn">
                        <FileDown className="h-3.5 w-3.5 mr-2" /> PDF
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={sendInvoice}
                              data-testid="send-invoice-btn">
                        <Mail className="h-3.5 w-3.5 mr-2" /> Invia al cliente
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      <FormDialog open={open} onOpenChange={setOpen} title="Aggiungi ricambio" fields={partFields}
                  initial={{ quantity: 1, chargeable: true }} onSubmit={addPart} testid="part-dialog" />
    </div>
  );
}
